import { QueryFunctionContext } from '@tanstack/react-query';
import { api } from '../services/api';
import { IData, IDataNoPagination } from '../types/shared';
import { ICartorio } from './useCartorios';

// ============================================================================
// Tipos
// ============================================================================

export type EstatutoStatus =
  | 'criado'
  | 'aguardando_preenchimento'
  | 'aguardando_validacao'
  | 'aguardando_validacao_cliente'
  | 'analise_cliente_concluida'
  | 'enviar_assinatura'
  | 'em_acompanhamento'
  | 'em_abertura_cnpj'
  | 'assinado_finalizado';

export type EstatutoFormatoDocumento = 'docx' | 'pdf';

export interface IEstatutoFormPayload {
  numero_turma: string;
  nome_curso: string;
  nome_instituicao: string;
  semestre_formatura: string;
  endereco_sede: string;
  vigencia_inicio: string;
  vigencia_fim: string;
  minimo_cargos_diretoria: number;
  reunioes_consecutivas_abandono: number;
  reunioes_alternadas_abandono: number;
  // Regras do Estatuto pela spec do jurídico (20/10). As opções vêm do back (schema_campos).
  periodo_reunioes_alternadas: string;
  periodicidade_balancete: 'mensal' | 'trimestral';
  balancete_trimestral_ate?: string | null;
  periodicidade_balanco: string;
  periodicidade_aprovacao_contas: string;
  prazo_contestacao_dias: number;
  limite_assembleias_extraordinarias: number;
  prazo_devolucao_dias: number;
  prazo_devolucao_desistencia_dias: number;
  ano_limite_admissao: number | null;
  prazo_limite_migracao_plano_anos: number;
  canal_comunicacao: string;
  eventos_formatura: string[];
  confirma_inexistencia_orgaos: boolean;
  // Constituição completa (20/10): fundadores, diretoria e assembleia → os 5 documentos.
  pessoas: IEstatutoPessoa[];
  data_assembleia: string;
  data_requerimento: string;
  cidade_assinatura: string;
  votos_favor: number | null;
  votos_contra: number;
  total_eleitores: number | null;
  presidente_trabalhos_cpf: string;
  secretario_trabalhos_cpf: string;
  // Formulário antigo; com `pessoas` o back copia do Presidente da lista.
  signatario_nome?: string;
  signatario_email?: string;
  signatario_cpf?: string;
}

/** Fundador. Diretoria = quem tem cargo; Lista de Presença = presentes. */
export interface IEstatutoPessoa {
  nome: string;
  // Concordância nos documentos (inscrito/inscrita, domiciliado/domiciliada...).
  sexo: 'M' | 'F' | '';
  nacionalidade: string;
  estado_civil: string;
  profissao: string;
  rg_numero: string;
  rg_orgao: string;
  cpf: string;
  endereco: string;
  email: string;
  cargo: string | null;
  presente: boolean;
}

export interface IEstatutoCampoMeta {
  // Constituição: keyof IEstatutoFormPayload; Baixa: os campos da etapa (vêm do back).
  name: string;
  label: string;
  // 'pessoas' = lista de IEstatutoPessoa (options = cargos); 'pessoa' = CPF escolhido nela.
  // Baixa: 'presentes' = associados presentes (IBaixaPresente); 'hora' = HH:MM.
  type:
    | 'text'
    | 'textarea'
    | 'date'
    | 'integer'
    | 'select'
    | 'boolean'
    | 'list'
    | 'pessoas'
    | 'pessoa'
    | 'presentes'
    | 'hora';
  required: boolean;
  default: unknown | null;
  options: string[] | null;
  help_text: string | null;
  min: number | null;
  max: number | null;
}

export interface IEstatuto {
  id: number;
  hashed_link: string;
  status: EstatutoStatus;
  titulo: string | null;
  destino?: EstatutoDestino;
  // Baixa: { origem, convocacao, assembleia, alertas } (ver IBaixaDados).
  dados_preenchidos: IEstatutoFormPayload | null;
  dt_envio_link: string | null;
  dt_preenchimento: string | null;
  expires_at: string | null;
  kanban_card_id: number | null;
  criado_por_id: number | null;
  // "Aguardando cliente" (T6.1 #766) — vem do card vinculado (o backend expõe no GET).
  aguardando_cliente?: boolean;
  motivo_bloqueio?: string | null;
  // Empresa vinculada (task #502) — usada p/ o estatuto aparecer na Área VIP do cliente.
  empresa_id?: number | null;
  // Nome (razão social) da empresa — o backend resolve no detalhe/PATCH p/ o card mostrar
  // o NOME ao reabrir, sem placeholder "Empresa #id". Ausente = cai no placeholder por id.
  empresa_nome?: string | null;
  // Cliente vinculado (#25) — usuário do portal (grupo_id=7). Vínculo direto e opcional,
  // além do por-empresa. O back serializa o id cru + o nome resolvido (cliente_nome).
  cliente_id?: number | null;
  // Nome do cliente — resolvido pelo backend (idem empresa_nome); sem ele o card reaberto
  // mostrava só "Cliente #id".
  cliente_nome?: string | null;
  // Cartório responsável pelo registro do estatuto.
  cartorio_id: number | null;
  cartorio?: ICartorio | null;
  // Dados do advogado (2º assinante). CPF/OAB são apenas registro nosso.
  advogado_nome: string | null;
  advogado_email: string | null;
  advogado_cpf: string | null;
  advogado_oab: string | null;
  // Presidente da comissão: recebe o link do formulário por e-mail.
  email_presidente?: string | null;
  // Responsáveis/supervisores do processo (vínculos com papel — Task #496).
  responsaveis?: { id: number; nome: string | null; papel?: string }[];
  supervisores?: { id: number; nome: string | null; papel?: string }[];
  dt_criacao: string;
  dt_atualizacao: string;
}

export interface IEstatutoPasso {
  titulo: string;
  descricao: string;
  campos: string[];
}

export type BaixaEtapa = 'convocacao' | 'assembleia';

/** O que o presidente vai preencher, sem token: a lista que a equipe manda antes do link. */
export interface IEstatutoPerguntas {
  constituicao: IEstatutoCampoMeta[];
  baixa: { etapa: BaixaEtapa; passos: IEstatutoPasso[]; schema_campos: IEstatutoCampoMeta[] }[];
}

/** Valor que a conferência por IA, no clique de enviar, pede para o presidente olhar (23/10). */
export interface IPontoConferencia {
  caminho: string;
  // Campo do formulário: leva ao passo dele.
  campo: string;
  onde: string;
  valor: string;
  problema: string;
}

export interface IEstatutoPublic {
  hashed_link: string;
  status: EstatutoStatus;
  titulo: string | null;
  expires_at: string | null;
  schema_campos: IEstatutoCampoMeta[];
  dados_preenchidos: IEstatutoFormPayload | null;
  // Baixa (21/10): formulário em 2 etapas, com os passos vindos do back.
  destino?: 'baixa';
  etapa?: BaixaEtapa;
  passos?: IEstatutoPasso[];
}

// Associado presente na assembleia de encerramento (a linha da tabela tem `presente`).
export interface IBaixaPresente {
  nome: string;
  cpf: string;
  email: string;
  sexo: 'M' | 'F' | '';
  adimplente: boolean;
  presente?: boolean;
}

// dados_preenchidos da Baixa: as 2 etapas como o back gravou e os alertas da spec (§7.2).
export interface IBaixaDados {
  origem?: Record<string, any>;
  convocacao?: Record<string, any>;
  assembleia?: Record<string, any> & { presentes: IBaixaPresente[] };
  alertas?: string[];
}

// O envio roda em segundo plano (1 documento por vez na Autentique): a resposta só diz o
// que foi; links e andamento vêm do status do card (aba Assinatura).
export interface IEstatutoAssinaturaResponse {
  documentos: { nome: string; assinantes: number }[];
}

export interface IEstatutoSyncResponse {
  status: 'pending' | 'rejeitado' | 'assinado';
}

export interface ICreateEstatutoData {
  titulo?: string;
  // Quadro de destino (task #492): 'constituicao' (default, quadro atual) | 'baixa' | 'alteracao'.
  destino?: EstatutoDestino;
  // Empresa vinculada (task #502) — para o estatuto aparecer na Área VIP do cliente.
  empresa_id?: number | null;
  expires_at?: string | null;
  kanban_card_id?: number | null;
  cartorio_id?: number | null;
  advogado_nome?: string | null;
  advogado_email?: string | null;
  advogado_cpf?: string | null;
  advogado_oab?: string | null;
  email_presidente?: string | null;
  // Baixa: constituição feita no sistema de onde vêm turma, presidente e associados.
  estatuto_origem_id?: number | null;
  // Responsável(eis) obrigatório (>=1) e supervisor(es) opcional — Task #496.
  responsavel_ids?: number[];
  supervisor_ids?: number[];
  // false (default) → cria só o card/processo (fica em "Criado").
  // true → já marca o link como enviado e move pra "Link Enviado" (com e-mail do
  // presidente, o back manda o link para ele).
  enviar_link?: boolean;
  // Etapa inicial (T12-A #779): cria o processo já numa etapa avançada (o backend posiciona
  // o card na coluna correspondente). Em etapa avançada, cartório/advogado ficam opcionais.
  // Omitir = fluxo normal (nasce em "Criado"). É o STATUS, não o título da coluna.
  etapa_inicial?: EstatutoStatus;
}

// Edição dos DADOS do processo (#14) — PATCH parcial: só o que vier é alterado.
// NÃO inclui destino/etapa/coluna (fora do escopo — não move o card no board).
export interface IEditarEstatutoData {
  titulo?: string;
  empresa_id?: number | null;
  cliente_id?: number | null;
  cartorio_id?: number | null;
  advogado_nome?: string | null;
  advogado_email?: string | null;
  advogado_cpf?: string | null;
  advogado_oab?: string | null;
  email_presidente?: string | null;
  responsavel_ids?: number[];
  supervisor_ids?: number[];
}

export interface IListEstatutosFilters {
  page?: number;
  size?: number;
  status?: EstatutoStatus | EstatutoStatus[];
  titulo?: string;
  criado_por_id?: number;
  dt_criacao_inicio?: string;
  dt_criacao_fim?: string;
}

export interface IEnviarAssinaturaPayload {
  signatario_nome?: string;
  signatario_email?: string;
  // Dados do advogado (2º assinante) — permite conferir/corrigir antes de enviar.
  advogado_nome?: string;
  advogado_email?: string;
  advogado_cpf?: string;
  advogado_oab?: string;
}

export interface IEstatutoDashboardEtapa {
  etapa: string;
  total: number;
}

/** Quadro do módulo — vai no path da rota de dashboard. */
export type EstatutoDestino = 'constituicao' | 'baixa' | 'alteracao';

export interface IEstatutoDashboardSla {
  no_prazo: number;
  em_risco: number;
  atrasado: number;
}

export interface IEstatutoDashboardAtencao {
  id: number;
  titulo: string;
  status: EstatutoStatus;
  dias_atraso: number;
}

export interface IEstatutoDashboard {
  total: number;
  finalizados: number;
  arquivados: number;
  por_etapa: IEstatutoDashboardEtapa[];
  // Quadro que originou os números — eco do path param, útil pra depurar cache.
  destino: EstatutoDestino;
  // KPIs que antes eram calculados no cliente (derivar()) varrendo a base inteira.
  // Agora vêm agregados do banco, já filtrados pelo quadro.
  atrasados: number;
  aguardando_cliente: number;
  finalizados_mes: number;
  tempo_medio_dias: number | null;
  taxa_conclusao: number;
  sla: IEstatutoDashboardSla;
  atencao: IEstatutoDashboardAtencao[];
}

export interface ISyncPendentesResponse {
  verificados: number;
  atualizados: Array<{ document_id: string; status: string }>;
}

// ============================================================================
// Constantes compartilhadas
// ============================================================================

export const ESTATUTO_STATUS_LABELS: Record<EstatutoStatus, string> = {
  criado: 'Criado',
  aguardando_preenchimento: 'Link Enviado / Aguardando Preenchimento',
  aguardando_validacao: 'Aguardando Validação',
  aguardando_validacao_cliente: 'Aguardando Validação do Cliente',
  analise_cliente_concluida: 'Análise do Cliente Concluída',
  enviar_assinatura: 'Enviar para Assinatura',
  em_acompanhamento: 'Acompanhamento do Processo Protocolado',
  em_abertura_cnpj: 'Abertura do CNPJ',
  assinado_finalizado: 'Assinado / Finalizado',
};

export const COLUNAS_ESTATUTO_ORDEM: EstatutoStatus[] = [
  'criado',
  'aguardando_preenchimento',
  'aguardando_validacao',
  'aguardando_validacao_cliente',
  'analise_cliente_concluida',
  'enviar_assinatura',
  'em_acompanhamento',
  'em_abertura_cnpj',
  'assinado_finalizado',
];

// Toda etapa cai aqui, mesmo sem ação própria: EstatutoAcoesSlot indexa este mapa
// direto pelo status do card, então uma chave faltando (undefined) quebra o slot
// (acoes.includes(...) em cima de undefined). Etapas manuais/de acompanhamento
// (T7/T10/T12-A) não têm ação própria, mas sempre permitem ver os dados e o PDF.
export const ACOES_POR_STATUS: Record<EstatutoStatus, string[]> = {
  criado: ['marcar_link_enviado'],
  aguardando_preenchimento: ['marcar_link_enviado', 'ver_link'],
  aguardando_validacao: ['validar', 'ver_dados', 'visualizar_pdf'],
  aguardando_validacao_cliente: ['ver_dados'],
  analise_cliente_concluida: ['validar', 'ver_dados'],
  enviar_assinatura: ['enviar_assinatura', 'sync_autentique', 'visualizar_pdf'],
  em_acompanhamento: ['visualizar_pdf', 'baixar_docx'],
  em_abertura_cnpj: ['visualizar_pdf', 'baixar_docx'],
  assinado_finalizado: ['visualizar_pdf', 'baixar_docx'],
};

/** Ações do card pelo status. A alteração ainda não tem formulário nem documento gerado (26/10). */
export const acoesDoEstatuto = (e: IEstatuto): string[] =>
  e.destino === 'alteracao' ? [] : (ACOES_POR_STATUS[e.status] ?? []);

// ============================================================================
// Hook
// ============================================================================

export const useEstatuto = () => {
  // -------- Endpoints públicos (sem auth) --------

  // Baixa: `etapa: 'convocacao'` reabre/regrava a 1ª parte (correção antes de enviar a 2ª).
  const getEstatutoPublic = async (
    token: string,
    etapa?: 'convocacao',
  ): Promise<IDataNoPagination<IEstatutoPublic>> => {
    const res = await api.get<IDataNoPagination<IEstatutoPublic>>(
      `/api/v1/estatuto/public/${token}`,
      { params: { etapa } },
    );
    return res.data;
  };

  // Prazo próprio: a IA do back responde em até ~20 s; passou, a tela envia sem a conferência.
  const conferirEstatutoPublic = async (
    token: string,
    payload: IEstatutoFormPayload,
    etapa?: 'convocacao',
  ): Promise<IPontoConferencia[]> => {
    const res = await api.post<IDataNoPagination<{ pontos: IPontoConferencia[] }>>(
      `/api/v1/estatuto/public/${token}/conferir`,
      payload,
      { params: { etapa }, timeout: 30000 },
    );
    return res.data.data?.pontos ?? [];
  };

  const submeterEstatutoPublic = async (
    token: string,
    payload: IEstatutoFormPayload,
    etapa?: 'convocacao',
  ): Promise<IDataNoPagination<IEstatutoPublic>> => {
    const res = await api.post<IDataNoPagination<IEstatutoPublic>>(
      `/api/v1/estatuto/public/${token}/submeter`,
      payload,
      { params: { etapa } },
    );
    return res.data;
  };

  // -------- Endpoints autenticados --------

  const listEstatutos = async (key: QueryFunctionContext): Promise<IData<IEstatuto[]>> => {
    const filters = (key.queryKey?.[1] ?? {}) as IListEstatutosFilters;
    const res = await api.get<IData<IEstatuto[]>>('/api/v1/estatuto/', {
      params: filters,
    });
    return res.data;
  };

  // O board do kanban não é paginado, mas GET /estatuto/ tem size máximo 100
  // (size > 100 → 400). Acumula todas as páginas antes do join card ↔ estatuto.
  const listAllEstatutos = async (): Promise<IEstatuto[]> => {
    const items: IEstatuto[] = [];
    let page = 1;
    for (;;) {
      // Sequencial de propósito: cada página depende do next_page da anterior.
      // eslint-disable-next-line no-await-in-loop
      const res = await api.get<IData<IEstatuto[]>>('/api/v1/estatuto/', {
        params: { page, size: 100 },
      });
      const d = res.data.data;
      items.push(...(d.items ?? []));
      if (d.next_page == null) break;
      page = d.next_page;
    }
    return items;
  };

  const getPerguntas = async (): Promise<IDataNoPagination<IEstatutoPerguntas>> => {
    const res = await api.get<IDataNoPagination<IEstatutoPerguntas>>('/api/v1/estatuto/perguntas');
    return res.data;
  };

  const getEstatuto = async (id: number): Promise<IDataNoPagination<IEstatuto>> => {
    const res = await api.get<IDataNoPagination<IEstatuto>>(`/api/v1/estatuto/${id}`);
    return res.data;
  };

  // Contagem de cards + KPIs do quadro. O destino vai no PATH — sem ele o backend
  // contava sempre a Constituição e o quadro da Baixa exibia os números do outro.
  const getDashboard = async (
    destino: EstatutoDestino,
  ): Promise<IDataNoPagination<IEstatutoDashboard>> => {
    const res = await api.get<IDataNoPagination<IEstatutoDashboard>>(
      `/api/v1/estatuto/dashboard/${destino}`,
    );
    return res.data;
  };

  const createEstatuto = async (
    data: ICreateEstatutoData,
  ): Promise<IDataNoPagination<IEstatuto>> => {
    const res = await api.post<IDataNoPagination<IEstatuto>>('/api/v1/estatuto/', data);
    return res.data;
  };

  // Edição dos dados do processo (#14): PATCH parcial — só os campos enviados mudam.
  const editarEstatuto = async (
    id: number,
    data: IEditarEstatutoData,
  ): Promise<IDataNoPagination<IEstatuto>> => {
    const res = await api.patch<IDataNoPagination<IEstatuto>>(`/api/v1/estatuto/${id}`, data);
    return res.data;
  };

  const marcarLinkEnviado = async (id: number): Promise<IDataNoPagination<IEstatuto>> => {
    const res = await api.post<IDataNoPagination<IEstatuto>>(
      `/api/v1/estatuto/${id}/marcar-link-enviado`,
    );
    return res.data;
  };

  const validarEstatuto = async (id: number): Promise<IDataNoPagination<IEstatuto>> => {
    const res = await api.post<IDataNoPagination<IEstatuto>>(`/api/v1/estatuto/${id}/validar`);
    return res.data;
  };

  const enviarAssinatura = async (
    id: number,
    payload?: IEnviarAssinaturaPayload,
  ): Promise<IDataNoPagination<IEstatutoAssinaturaResponse>> => {
    const res = await api.post<IDataNoPagination<IEstatutoAssinaturaResponse>>(
      `/api/v1/estatuto/${id}/enviar-assinatura`,
      payload ?? {},
    );
    return res.data;
  };

  // Correção de dado de pessoa pela equipe (23/10): {"pessoas[3].email": "..."}.
  const corrigirPessoa = async (
    id: number,
    campos: Record<string, string>,
  ): Promise<
    IDataNoPagination<{
      mudou: string[];
      trocados: string[];
      refeitos: string[];
      falhas: string[];
      assinados: string[];
    }>
  > => {
    const res = await api.post(`/api/v1/estatuto/${id}/corrigir-pessoa`, { campos });
    return res.data;
  };

  const syncAutentique = async (id: number): Promise<IDataNoPagination<IEstatutoSyncResponse>> => {
    const res = await api.post<IDataNoPagination<IEstatutoSyncResponse>>(
      `/api/v1/estatuto/${id}/sync-autentique`,
    );
    return res.data;
  };

  const downloadDocumento = async (
    id: number,
    formato: EstatutoFormatoDocumento,
  ): Promise<Blob> => {
    const res = await api.get(`/api/v1/estatuto/${id}/documento`, {
      params: { formato },
      responseType: 'blob',
    });
    return res.data as Blob;
  };

  // Sincroniza no backend todos os documentos Autentique pendentes (recupera
  // webhooks perdidos). Pensado pra ser chamado ao abrir o board, sem bloquear
  // a renderização — as mudanças chegam via WS e/ou na resposta.
  const syncPendentesAutentique = async (): Promise<IDataNoPagination<ISyncPendentesResponse>> => {
    const res = await api.post<IDataNoPagination<ISyncPendentesResponse>>(
      '/api/v1/autentique/sync-pendentes',
    );
    return res.data;
  };

  return {
    // Públicos
    getEstatutoPublic,
    conferirEstatutoPublic,
    submeterEstatutoPublic,
    // Autenticados
    listEstatutos,
    listAllEstatutos,
    getPerguntas,
    getEstatuto,
    getDashboard,
    createEstatuto,
    editarEstatuto,
    marcarLinkEnviado,
    validarEstatuto,
    enviarAssinatura,
    corrigirPessoa,
    syncAutentique,
    downloadDocumento,
    syncPendentesAutentique,
  };
};

// ============================================================================
// Helpers
// ============================================================================

/**
 * Monta a URL pública pra envio externo (WhatsApp/email).
 * Usa window.location.origin para garantir host correto.
 */
export const buildEstatutoPublicUrl = (hashedLink: string): string => {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}/estatuto/preencher/${hashedLink}`;
};

/**
 * Dispara download de um Blob via URL.createObjectURL.
 */
export const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
